import { useMemo, useState } from "react";
import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { CardMember, Category } from "@/lib/types";
import { SkeletonCard } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { contrastText, validColor } from "@/lib/color";
import MemberCard from "@/components/feature/MemberCard";
import MemberCardModal from "@/components/feature/MemberCardModal";
import DeptFilter from "@/components/feature/DeptFilter";
import { useDepartments } from "@/hooks/useDepartments";

export default function CardsPage() {
  const { data, loading, error, reload } = useApi<CardMember[]>("cards.php?size=100");
  const { data: categories } = useApi<Category[]>("categories.php");
  const departments = useDepartments();

  const [search, setSearch] = useState("");
  const [cate, setCate] = useState("");
  const [dept, setDept] = useState("");
  const [openNo, setOpenNo] = useState<number | null>(null);

  const cards = data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards.filter((c) => {
      if (cate && (c.cate_name || "") !== cate) return false;
      if (dept && String(c.dept || "") !== dept) return false;
      if (!q) return true;
      return [c.name, c.aho, c.biz_name]
        .some((v) => String(v ?? "").toLowerCase().includes(q));
    });
  }, [cards, search, cate, dept]);

  const openIndex = filtered.findIndex((c) => c.no === openNo);

  const handlePrev = () => {
    if (openIndex < 0) return;
    const prev = (openIndex - 1 + filtered.length) % filtered.length;
    setOpenNo(filtered[prev].no);
  };
  const handleNext = () => {
    if (openIndex < 0) return;
    const next = (openIndex + 1) % filtered.length;
    setOpenNo(filtered[next].no);
  };

  return (
    <>
      <PageBanner
        title="명함"
        description="회원님들의 디지털 명함을 한눈에 확인하세요."
        breadcrumb={[{ label: "명함" }]}
      />

      <section className="mx-auto max-w-6xl px-4 md:px-6 py-10 md:py-12">
        {/* 검색 + 필터 */}
        <div className="flex flex-col gap-4">
          <div className="relative">
            <i
              className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"
              aria-hidden="true"
            ></i>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="이름 · 아호 · 직장명 검색"
              className="w-full rounded-full border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCate("")}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                cate === ""
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
              }`}
            >
              전체
            </button>
            {(categories ?? []).map((c) => {
              const active = cate === c.name;
              const color = validColor(c.color) || "#17408B";
              return (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => setCate(c.name)}
                  className="whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-opacity hover:opacity-85"
                  style={
                    active
                      ? { backgroundColor: color, color: contrastText(color) }
                      : undefined
                  }
                >
                  <span className={active ? "" : "text-foreground-600"}>
                    {c.name}
                    {typeof c.cnt === "number" ? ` ${c.cnt}` : ""}
                  </span>
                </button>
              );
            })}
          </div>

          <DeptFilter departments={departments} value={dept} onChange={setDept} />
        </div>

        {/* 목록 */}
        <div className="mt-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : error ? (
            <EmptyState
              icon="ri-error-warning-line"
              title="명함을 불러오지 못했습니다"
              description={error}
              action={
                <button
                  type="button"
                  onClick={reload}
                  className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
                >
                  다시 시도
                </button>
              }
            />
          ) : filtered.length === 0 ? (
            cards.length === 0 ? (
              <EmptyState
                icon="ri-id-card-line"
                title="아직 등록된 명함이 없습니다"
              />
            ) : (
              <EmptyState
                icon="ri-id-card-line"
                title="조건에 맞는 명함이 없습니다"
                description="검색어나 업종 필터를 변경해 보세요."
              />
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((card) => (
                <MemberCard key={card.no} card={card} onOpen={(c) => setOpenNo(c.no)} />
              ))}
            </div>
          )}
        </div>
      </section>

      <MemberCardModal
        no={openNo}
        onClose={() => setOpenNo(null)}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </>
  );
}